import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, Platform, type TextStyle, type ViewStyle } from 'react-native';
import moment from 'moment';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { androidFlatSurfaceStyle, androidScaleSize } from '../../shared/theme/androidUiParity';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../../shared/theme/sharedStyles';
import { createSheetFooterDividerStyle } from '../../shared/theme/styles/sheets';
import { CalendarWeekdayStrip } from '../../components/ui';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { useTranslation, getLocale, formatNumber } from '../../shared/i18n';
import { setDeliveryAddressDates } from '../../data/domains/addresses';
import { getToastStore } from '../../components/toast/toastStore';
import type { SubscriptionData } from '../../data/domains/subscription';
import {
  buildWeekStripDays,
  generateDeliveryAddressRequests,
  getSubscriptionLabel,
  type DeliveryDayCellState,
  type WeekStripDay,
} from '../../data/domains/addresses';
import { MainLoader } from '../../components/load-main/MainLoader';
import { ensureAsciiDateString } from '../../shared/utils/apiParams';
import { normalizeCalendarDate } from '../../shared/utils/dateUtils';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const FOOTER_BTN_HEIGHT = androidScaleSize(48);

export type AddressDeliverySheetChromeProps = {
  visible: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  sheetStyle?: ViewStyle;
  frostedHeader?: boolean;
  topBorderRadius?: number;
};

export type AddressDeliveryAssignmentSheetCoreProps = {
  visible: boolean;
  onClose: (refreshNeeded: boolean) => void;
  address: { id: number; name?: string };
  activeSubscriptions: SubscriptionData[];
  availableDates: string[];
  presetDates: string[];
  customerId: number;
  SheetChrome: React.ComponentType<AddressDeliverySheetChromeProps>;
};

function createStyles(c: ThemeColors) {
  const instruction_bg = (hexToRgba(c.primary, 0.08) as string) || c.primaryUltra;

  return StyleSheet.create({
    body: { width: '100%' },
    instructionCard: {
      marginHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.lg,
      backgroundColor: instruction_bg,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: (hexToRgba(c.primary, 0.22) as string) || c.border,
      gap: appTheme.spacing.xs,
    },
    instructionTitle: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    instructionHint: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      lineHeight: 18,
      ...ANDROID_TEXT_COMPACT,
    },
    errorBanner: {
      marginHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
      padding: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: (hexToRgba(c.error, 0.1) as string) || c.errorFade,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: (hexToRgba(c.error, 0.25) as string) || c.error,
    },
    errorText: {
      ...appTheme.typography.caption,
      color: c.error,
      ...ANDROID_TEXT_COMPACT,
    },
    tabRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: appTheme.spacing.xs,
      paddingHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
    },
    tabChip: {
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.xs,
      borderRadius: appTheme.borderRadius.pill,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderLight,
      backgroundColor: c.surface,
      maxWidth: '100%',
    },
    tabChipActive: {
      backgroundColor: c.primaryUltra,
      borderColor: c.primary,
    },
    tabChipText: {
      ...appTheme.typography.caption,
      fontWeight: '600',
      color: c.textSecondary,
      ...ANDROID_TEXT_COMPACT,
    },
    tabChipTextActive: {
      color: c.primary,
      fontWeight: '700',
    },
    weekNavRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.xs,
    },
    weekNavLabel: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    weekNavBtn: {
      minWidth: 40,
      minHeight: 40,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: appTheme.borderRadius.md,
    },
    weekNavBtnText: {
      fontSize: 20,
      fontWeight: '700',
      color: c.primary,
      ...ANDROID_TEXT_COMPACT,
    },
    weekNavBtnDisabled: { opacity: 0.35 },
    weekDaysRow: {
      flexDirection: 'row',
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.xs,
      marginHorizontal: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: (hexToRgba(c.text, 0.04) as string) || c.surfaceLight,
      ...horizontalLtrDirectionStyle,
    },
    weekDayCell: { flex: 1, alignItems: 'center' },
    weekDayText: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '800',
      letterSpacing: 0.3,
      ...ANDROID_TEXT_COMPACT,
    },
    weekStrip: {
      flexDirection: 'row',
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      marginHorizontal: appTheme.spacing.sm,
      gap: appTheme.spacing.xs,
      ...horizontalLtrDirectionStyle,
    },
    weekStripCell: {
      flex: 1,
      minHeight: 56,
      borderRadius: appTheme.borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xs,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderLight,
      backgroundColor: c.surface,
    },
    weekStripCellAssigned: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    weekStripCellAvailable: {
      backgroundColor: (hexToRgba(c.primary, 0.08) as string) || c.primaryUltra,
      borderColor: (hexToRgba(c.primary, 0.22) as string) || c.primaryFade,
    },
    weekStripCellOffPlan: {
      backgroundColor: (hexToRgba(c.warning, 0.12) as string) || c.warningUltra,
      borderColor: (hexToRgba(c.warning, 0.25) as string) || c.warning,
    },
    weekStripCellDisabled: { opacity: 0.38 },
    weekStripWeekday: {
      fontSize: 10,
      fontWeight: '700',
      color: c.textSecondary,
      marginBottom: 2,
      ...ANDROID_TEXT_COMPACT,
    },
    weekStripWeekdayOnPrimary: { color: c.onPrimary ?? c.white },
    weekStripDayNum: {
      fontSize: 16,
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    weekStripDayNumOnPrimary: { color: c.onPrimary ?? c.white },
    legendRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.lg,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.md,
    },
    legendItem: { flexDirection: 'row', alignItems: 'center', gap: appTheme.spacing.xs },
    legendSwatch: { width: 12, height: 12, borderRadius: 6 },
    legendText: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '600',
      ...ANDROID_TEXT_COMPACT,
    },
    footer: {
      paddingHorizontal: appTheme.spacing.lg,
      backgroundColor: c.surface,
      ...createSheetFooterDividerStyle(c),
    },
    footerRow: { flexDirection: 'row', gap: appTheme.spacing.sm, alignItems: 'stretch' },
    footerGrow: { flex: 1, minWidth: 0 },
    footerBtnShell: {
      width: '100%',
      minHeight: FOOTER_BTN_HEIGHT,
      borderRadius: appTheme.borderRadius.lg,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.sm,
      ...Platform.select({
        android: androidFlatSurfaceStyle(),
      }),
    },
    btnDisabled: { opacity: 0.5 },
  });
}

function cellStyleForState(state: DeliveryDayCellState, styles: ReturnType<typeof createStyles>) {
  switch (state) {
    case 'assigned':
      return styles.weekStripCellAssigned;
    case 'available':
      return styles.weekStripCellAvailable;
    case 'off_plan':
      return styles.weekStripCellOffPlan;
    default:
      return styles.weekStripCellDisabled;
  }
}

const WeekStripDayCell = React.memo(function WeekStripDayCell({
  day,
  onToggle,
  styles,
}: {
  day: WeekStripDay;
  onToggle: (date: string, state: DeliveryDayCellState) => void;
  styles: ReturnType<typeof createStyles>;
}) {
  const is_assigned = day.state === 'assigned';
  const is_tappable = day.state === 'assigned' || day.state === 'available';

  return (
    <Pressable
      disabled={!is_tappable}
      onPress={() => onToggle(day.date, day.state)}
      style={[styles.weekStripCell, cellStyleForState(day.state, styles), day.state === 'disabled' && styles.weekStripCellDisabled]}
      accessibilityRole="button"
      accessibilityState={{ selected: is_assigned, disabled: !is_tappable }}>
      <Text style={[styles.weekStripWeekday, is_assigned && styles.weekStripWeekdayOnPrimary]}>{day.weekday_label}</Text>
      <Text style={[styles.weekStripDayNum, is_assigned && styles.weekStripDayNumOnPrimary]}>{formatNumber(day.day_of_month)}</Text>
    </Pressable>
  );
});

export function AddressDeliveryAssignmentSheetCore({
  visible,
  onClose,
  address,
  activeSubscriptions,
  availableDates,
  presetDates,
  customerId,
  SheetChrome,
}: AddressDeliveryAssignmentSheetCoreProps) {
  const { t } = useTranslation();
  const locale = getLocale();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(colors),
      label: createStackedFooterPrimaryLabelStyle(colors),
    }),
    [colors],
  );
  const secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(colors),
      label: createStackedFooterSecondaryLabelStyle(colors),
    }),
    [colors],
  );

  const subs = useMemo(() => (Array.isArray(activeSubscriptions) ? activeSubscriptions : []), [activeSubscriptions]);
  const has_multi_sub = subs.length > 1;
  const [active_sub_index, setActiveSubIndex] = useState(0);
  const active_sub = subs[active_sub_index] ?? subs[0] ?? null;
  const [week_start, setWeekStart] = useState(() => moment().startOf('week'));
  const [selected_dates, setSelectedDates] = useState<string[]>([]);
  const [is_submitting, setIsSubmitting] = useState(false);
  const [inline_error, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setSelectedDates(
        presetDates
          .map(d => ensureAsciiDateString(d) || normalizeCalendarDate(d))
          .filter(Boolean),
      );
      setWeekStart(moment().startOf('week'));
      setActiveSubIndex(0);
      setInlineError(null);
      setIsSubmitting(false);
    }
  }, [visible, presetDates]);

  const available_dates_ascii = useMemo(
    () =>
      availableDates
        .map(d => ensureAsciiDateString(d) || normalizeCalendarDate(d))
        .filter(Boolean),
    [availableDates],
  );

  const week_days = useMemo(
    () => buildWeekStripDays(week_start, available_dates_ascii, selected_dates, active_sub, locale),
    [week_start, available_dates_ascii, selected_dates, active_sub, locale],
  );

  const weekday_labels = useMemo(() => week_days.map(d => d.weekday_label), [week_days]);

  const week_label = useMemo(() => {
    const end = week_start.clone().add(6, 'day');
    const loc = locale === 'ar' ? 'ar' : 'en';
    return `${week_start.clone().locale(loc).format('D MMM')} – ${end.locale(loc).format('D MMM')}`;
  }, [week_start, locale]);

  const can_go_prev = useMemo(() => {
    const prev = week_start.clone().subtract(1, 'week');
    const end = prev.clone().add(6, 'day');
    return end.isSameOrAfter(moment().startOf('day'), 'day');
  }, [week_start]);

  const can_go_next = useMemo(() => {
    if (available_dates_ascii.length === 0) {
      return false;
    }
    const last = moment(available_dates_ascii[available_dates_ascii.length - 1], 'YYYY-MM-DD', true);
    return week_start.clone().add(1, 'week').isSameOrBefore(last, 'day');
  }, [week_start, available_dates_ascii]);

  const toggleDate = useCallback((date_str: string, state: DeliveryDayCellState) => {
    if (state !== 'assigned' && state !== 'available') {
      return;
    }
    setInlineError(null);
    const ascii = ensureAsciiDateString(date_str) || normalizeCalendarDate(date_str) || date_str;
    setSelectedDates(prev => (prev.includes(ascii) ? prev.filter(d => d !== ascii) : [...prev, ascii]));
  }, []);

  const handleSave = useCallback(async () => {
    if (selected_dates.length === 0) {
      setInlineError(t('address.deliveryDatesNoSubscription'));
      return;
    }
    const requests = generateDeliveryAddressRequests(subs, selected_dates, customerId, address.id);
    if (requests.length === 0) {
      setInlineError(t('address.deliveryDatesNoSubscription'));
      return;
    }
    setIsSubmitting(true);
    setInlineError(null);
    try {
      await setDeliveryAddressDates(requests);
      getToastStore().show(t('address.deliverySavedSuccess'), { variant: 'success' });
      onClose(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('address.deliveryDatesError');
      setInlineError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [selected_dates, subs, customerId, address.id, onClose, t]);

  const handleDismiss = useCallback(() => onClose(false), [onClose]);

  const selected_count = selected_dates.length;
  const can_save = selected_count > 0 && !is_submitting;
  const title = t('address.deliveryAssignmentTitle');

  const footer = (
    <View style={styles.footer}>
      <View style={styles.footerRow}>
        <View style={styles.footerGrow}>
          <Pressable onPress={handleDismiss} accessibilityRole="button" accessibilityLabel={t('common.cancel')}>
            <View style={[secondary_cta.surface, styles.footerBtnShell]}>
              <Text style={secondary_cta.label}>{t('common.cancel')}</Text>
            </View>
          </Pressable>
        </View>
        <View style={styles.footerGrow}>
          <Pressable
            onPress={handleSave}
            disabled={!can_save}
            accessibilityRole="button"
            accessibilityLabel={t('common.save')}>
            <View style={[primary_cta.surface, styles.footerBtnShell, !can_save && styles.btnDisabled]}>
              {is_submitting ? (
                <MainLoader size={18} color={colors.onPrimary ?? colors.white} />
              ) : (
                <Text style={primary_cta.label}>{t('common.save')}</Text>
              )}
            </View>
          </Pressable>
        </View>
      </View>
    </View>
  );

  return (
    <SheetChrome visible={visible} onClose={handleDismiss} title={title} footer={footer}>
      <ScrollView style={styles.body} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false} bounces={false}>
        <View style={styles.instructionCard}>
          <Text style={styles.instructionTitle}>
            {address.name || t('address.addressFallback')}
          </Text>
          <Text style={styles.instructionHint}>{t('address.deliveryAssignmentExplainer')}</Text>
        </View>

        {inline_error ? (
          <View style={styles.errorBanner}>
            <Text style={styles.errorText}>{inline_error}</Text>
          </View>
        ) : null}

        {has_multi_sub ? (
          <View style={styles.tabRow}>
            {subs.map((sub, idx) => {
              const label = getSubscriptionLabel(sub, t('address.subscriptionTabFallback'));
              const is_active = idx === active_sub_index;
              return (
                <Pressable
                  key={String(sub.id ?? idx)}
                  onPress={() => setActiveSubIndex(idx)}
                  style={[styles.tabChip, is_active && styles.tabChipActive]}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: is_active }}>
                  <Text style={[styles.tabChipText, is_active && styles.tabChipTextActive]} numberOfLines={1}>
                    {label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : null}

        <View style={styles.weekNavRow}>
          <Pressable
            onPress={() => can_go_prev && setWeekStart(prev => prev.clone().subtract(1, 'week'))}
            disabled={!can_go_prev}
            style={[styles.weekNavBtn, !can_go_prev && styles.weekNavBtnDisabled]}
            accessibilityLabel={t('calendar.previousMonth')}>
            <Text style={styles.weekNavBtnText}>‹</Text>
          </Pressable>
          <Text style={styles.weekNavLabel}>{week_label}</Text>
          <Pressable
            onPress={() => can_go_next && setWeekStart(prev => prev.clone().add(1, 'week'))}
            disabled={!can_go_next}
            style={[styles.weekNavBtn, !can_go_next && styles.weekNavBtnDisabled]}
            accessibilityLabel={t('calendar.nextMonth')}>
            <Text style={styles.weekNavBtnText}>›</Text>
          </Pressable>
        </View>

        <CalendarWeekdayStrip labels={weekday_labels} rowStyle={styles.weekDaysRow} cellStyle={styles.weekDayCell} textStyle={styles.weekDayText} />

        <View style={styles.weekStrip}>
          {week_days.map(day => (
            <WeekStripDayCell key={day.date} day={day} onToggle={toggleDate} styles={styles} />
          ))}
        </View>

        <View style={styles.legendRow}>
          <View style={styles.legendItem}>
            <View style={[styles.legendSwatch, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>{t('address.legendAssigned')}</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendSwatch, { backgroundColor: (hexToRgba(colors.primary, 0.15) as string) || colors.primaryUltra, borderWidth: 1, borderColor: colors.primaryFade }]} />
            <Text style={styles.legendText}>{t('address.legendAvailable')}</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendSwatch, { backgroundColor: (hexToRgba(colors.warning, 0.2) as string) || colors.warningUltra }]} />
            <Text style={styles.legendText}>{t('address.legendOffPlan')}</Text>
          </View>
        </View>
      </ScrollView>
    </SheetChrome>
  );
}
